import React, { useState } from 'react';
import { Form, Input, Button, Spin } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  ArrowRightOutlined,
  CloudServerOutlined,
  CodeOutlined,
  GlobalOutlined,
  GithubOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import './Login.css';

const BRAND_POINTS = [
  { icon: <CloudServerOutlined />, title: 'MQTT 实时接入', desc: '边缘设备长连接上报，毫秒级同步' },
  { icon: <CodeOutlined />, title: 'Web SSH 终端', desc: 'FRP 反向隧道，浏览器直连 root shell' },
  { icon: <GlobalOutlined />, title: 'PPPoE 多实例拨号', desc: 'macvlan + 策略路由，单机多链路并发' },
];

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login, loading } = useAuthStore();
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleSubmit = async (values: { username: string; password: string }) => {
    setLoginError(null);
    const success = await login(values.username, values.password);
    if (success) {
      navigate('/');
    } else {
      setLoginError('用户名或密码错误，请重试');
    }
  };

  return (
    <div className="login-page">
      {/* 背景装饰 */}
      <div className="lbg-grid" />
      <div className="lbg-glow lbg-glow-1" />
      <div className="lbg-glow lbg-glow-2" />
      <div className="lbg-scanline" />

      {/* 左侧品牌墙 */}
      <aside className="login-brand">
        <div className="brand-top">
          <button className="brand-home" onClick={() => navigate('/home')}>
            <HomeOutlined /> 返回首页
          </button>
          <div className="brand-logo-row">
            <span className="logo-bracket">[</span>
            <span className="logo-text">LLCP</span>
            <span className="logo-bracket">]</span>
            <span className="brand-version">v0.1.1</span>
          </div>
        </div>

        <div className="brand-center">
          <div className="brand-badge">
            <span className="brand-pulse" />
            <span>SECURE ACCESS</span>
          </div>
          <h1 className="brand-title">
            <span className="bt-zh">Linux 链路控制面板</span>
            <span className="bt-en">Linux Link Control Panel</span>
          </h1>
          <p className="brand-slogan">
            面向边缘 Linux 设备的一体化链路控制平台，
            <br />
            让分布式设备管理像调用本地 API 一样简单。
          </p>

          <div className="brand-points">
            {BRAND_POINTS.map((p) => (
              <div className="brand-point" key={p.title}>
                <div className="bp-icon">{p.icon}</div>
                <div className="bp-text">
                  <div className="bp-title">{p.title}</div>
                  <div className="bp-desc">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="brand-bottom">
          <div className="brand-terminal">
            <span className="bt-prompt">$</span>
            <span className="bt-cmd">llcp --status</span>
            <span className="bt-ok">● online</span>
          </div>
          <div className="brand-copy">© 2026 LLCP · NestJS + React + Go</div>
        </div>
      </aside>

      {/* 右侧登录表单 */}
      <main className="login-panel">
        <div className="login-card-glass">
          <div className="lc-head">
            <div className="lc-icon">
              <LockOutlined />
            </div>
            <h2 className="lc-title">欢迎回来</h2>
            <p className="lc-sub">登录以进入 LLCP 控制台</p>
          </div>

          <Spin spinning={loading}>
            <Form
              form={form}
              name="login"
              className="login-form-dark"
              initialValues={{ remember: true }}
              onFinish={handleSubmit}
              size="large"
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input
                  prefix={<UserOutlined className="input-icon" />}
                  placeholder="用户名"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined className="input-icon" />}
                  placeholder="密码"
                  autoComplete="current-password"
                />
              </Form.Item>

              {loginError && <div className="login-error">{loginError}</div>}

              <Form.Item className="login-submit-item">
                <Button type="primary" htmlType="submit" className="login-submit" loading={loading}>
                  登录控制台 <ArrowRightOutlined />
                </Button>
              </Form.Item>
            </Form>
          </Spin>

          <div className="lc-foot">
            <a href="https://github.com/myiis/llcp" target="_blank" rel="noreferrer">
              <GithubOutlined /> 项目源码
            </a>
            <span className="lc-divider">·</span>
            <a href="http://127.0.0.1:3000/api/docs" target="_blank" rel="noreferrer">
              API 文档
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
